/**
 * Shared validation helpers (email, mobile, etc.) using Zod.
 * Reusable across features; messages can be overridden via options.
 */

import { z } from "zod";

// ---------------------------------------------------------------------------
// Zod schemas (internal)
// ---------------------------------------------------------------------------

/** Non-empty trimmed string that passes Zod's email format */
const emailSchema = z
  .string()
  .trim()
  .min(1)
  .email();

/** India mobile: 10 digits, first digit 6–9. Input is normalized (trim, remove spaces and +91). */
function normalizeMobileIndia(value: string): string {
  return value.trim().replace(/\s/g, "").replace(/^\+91\s?/, "");
}

const mobileIndiaSchema = z
  .string()
  .transform(normalizeMobileIndia)
  .refine((s) => /^[6-9][0-9]{9}$/.test(s));

/** Value that is either a valid email or a valid India mobile (for OTP / contact field) */
const emailOrPhoneSchema = z.union([emailSchema, mobileIndiaSchema]);

/** Signup form schema: name, email, mobile (all required), and terms acceptance (required) */
const signupFormSchema = z.object({
  name: z.string().trim().min(1),
  email: emailSchema,
  mobile: mobileIndiaSchema,
  acceptTerms: z.boolean().refine((val) => val === true, {
    message: "Please accept the Terms of Service and Privacy Policy to create an account.",
  }),
});

/** OTP: exactly 4 digits */
const otpSchema = z
  .string()
  .trim()
  .length(4, "OTP must be 4 digits")
  .regex(/^[0-9]{4}$/, "OTP must be 4 digits");

/** Password: non-empty (for required check only) */
const passwordRequiredSchema = z.string().trim().min(1, "Password is required");

// ---------------------------------------------------------------------------
// Public boolean checks (no custom messages)
// ---------------------------------------------------------------------------

export function isValidEmail(value: string): boolean {
  return emailSchema.safeParse(value.trim()).success;
}

/** India mobile: exactly 10 digits, starting with 6, 7, 8 or 9 */
export function isValidMobileIndia(value: string): boolean {
  return mobileIndiaSchema.safeParse(value).success;
}

/** OTP: exactly 4 numeric digits */
export function isValidOtp(value: string): boolean {
  return otpSchema.safeParse(value.trim()).success;
}

// ---------------------------------------------------------------------------
// Options and validate functions (same API as before)
// ---------------------------------------------------------------------------

export interface ValidateEmailOptions {
  required?: boolean;
  requiredMessage?: string;
  invalidMessage?: string;
}

export interface ValidateMobileOptions {
  required?: boolean;
  requiredMessage?: string;
  invalidMessage?: string;
}

export interface ValidateEmailOrPhoneOptions {
  requiredMessage?: string;
  invalidEmailMessage?: string;
  invalidMobileMessage?: string;
}

export interface ValidateOtpOptions {
  requiredMessage?: string;
  invalidMessage?: string;
}

export interface ValidatePasswordRequiredOptions {
  requiredMessage?: string;
}

export interface ValidateSignupFormOptions {
  nameRequiredMessage?: string;
  emailRequiredMessage?: string;
  emailInvalidMessage?: string;
  mobileRequiredMessage?: string;
  mobileInvalidMessage?: string;
  termsErrorMessage?: string;
}

export interface SignupFormValidationResult {
  nameError: string | null;
  emailError: string | null;
  mobileError: string | null;
  termsError: string | null;
}

export function validateEmail(
  value: string,
  options: ValidateEmailOptions = {}
): string | null {
  const {
    required = true,
    requiredMessage = "Please enter your email",
    invalidMessage = "Please enter a valid email address",
  } = options;
  const trimmed = value.trim();
  if (!trimmed) return required ? requiredMessage : null;
  const result = emailSchema.safeParse(trimmed);
  return result.success ? null : invalidMessage;
}

export function validateMobileIndia(
  value: string,
  options: ValidateMobileOptions = {}
): string | null {
  const {
    required = true,
    requiredMessage = "Please enter your mobile number",
    invalidMessage = "Please enter a valid 10-digit mobile number",
  } = options;
  const normalized = normalizeMobileIndia(value);
  if (!normalized) return required ? requiredMessage : null;
  const result = mobileIndiaSchema.safeParse(value);
  return result.success ? null : invalidMessage;
}

/**
 * Validates a value that can be either email or mobile (e.g. OTP / contact field) using Zod.
 * Uses a union of email and India-mobile schemas; picks error message from options by input shape.
 */
export function validateEmailOrPhone(
  value: string,
  options: ValidateEmailOrPhoneOptions = {}
): string | null {
  const {
    requiredMessage = "Please enter your email or mobile number",
    invalidEmailMessage = "Please enter a valid email address",
    invalidMobileMessage = "Please enter a valid 10-digit mobile number",
  } = options;
  const trimmed = value.trim();
  if (!trimmed) return requiredMessage;
  const result = emailOrPhoneSchema.safeParse(trimmed);
  if (result.success) return null;
  return trimmed.includes("@") ? invalidEmailMessage : invalidMobileMessage;
}

/**
 * Validates OTP (4 digits) using Zod.
 */
export function validateOtp(value: string, options: ValidateOtpOptions = {}): string | null {
  const {
    requiredMessage = "Please enter the OTP",
    invalidMessage = "Please enter a valid 4-digit OTP",
  } = options;
  const trimmed = value.trim();
  if (!trimmed) return requiredMessage;
  const result = otpSchema.safeParse(trimmed);
  return result.success ? null : invalidMessage;
}

/**
 * Validates password is required (non-empty) using Zod.
 */
export function validatePasswordRequired(
  value: string,
  options: ValidatePasswordRequiredOptions = {}
): string | null {
  const { requiredMessage = "Please enter your password" } = options;
  const result = passwordRequiredSchema.safeParse(value);
  return result.success ? null : requiredMessage;
}

/**
 * Validates a complete signup form (name, email, mobile, terms) using Zod.
 * Returns error messages for each field, or null if valid.
 */
export function validateSignupForm(
  name: string,
  email: string,
  mobile: string,
  acceptTerms: boolean,
  options: ValidateSignupFormOptions = {}
): SignupFormValidationResult {
  const {
    nameRequiredMessage = "Please enter your name",
    emailRequiredMessage = "Please enter your email address",
    emailInvalidMessage = "Please enter a valid email address",
    mobileRequiredMessage = "Please enter your mobile number",
    mobileInvalidMessage = "Please enter a valid 10-digit mobile number",
    termsErrorMessage = "Please accept the Terms of Service and Privacy Policy to create an account.",
  } = options;

  const trimmedName = name.trim();
  const trimmedEmail = email.trim();
  const trimmedMobile = mobile.trim();

  // Check required fields first
  if (!trimmedName) {
    return {
      nameError: nameRequiredMessage,
      emailError: null,
      mobileError: null,
      termsError: null,
    };
  }
  if (!trimmedEmail) {
    return {
      nameError: null,
      emailError: emailRequiredMessage,
      mobileError: null,
      termsError: null,
    };
  }
  if (!trimmedMobile) {
    return {
      nameError: null,
      emailError: null,
      mobileError: mobileRequiredMessage,
      termsError: null,
    };
  }

  // Use Zod schema to validate all fields together
  const result = signupFormSchema.safeParse({
    name: trimmedName,
    email: trimmedEmail,
    mobile: trimmedMobile,
    acceptTerms,
  });

  if (result.success) {
    return { nameError: null, emailError: null, mobileError: null, termsError: null };
  }

  // Extract field-specific errors from Zod
  const errors = result.error.flatten().fieldErrors;

  return {
    nameError: errors.name ? nameRequiredMessage : null,
    emailError: errors.email ? (trimmedEmail ? emailInvalidMessage : emailRequiredMessage) : null,
    mobileError: errors.mobile ? mobileInvalidMessage : null,
    termsError: errors.acceptTerms ? termsErrorMessage : null,
  };
}

// ---------------------------------------------------------------------------
// Masking (unchanged; not validation)
// ---------------------------------------------------------------------------

/**
 * Options to configure email masking behaviour.
 *
 * By default we:
 * - keep the first 2 characters of the local part (before "@")
 * - keep the first character of the domain (after "@")
 * - mask everything else with "*"
 */
export interface MaskEmailOptions {
  /** How many characters to keep at the start of the local part (before "@"). Default: 2 */
  localVisiblePrefix?: number;
  /** How many characters to keep at the end of the local part. Default: 0 */
  localVisibleSuffix?: number;
  /** How many characters to keep at the start of the domain (excluding dots). Default: 1 */
  domainVisiblePrefix?: number;
  /** How many characters to keep at the end of the domain (including TLD). Default: 0 */
  domainVisibleSuffix?: number;
  /** Masking character. Default: "*" */
  maskChar?: string;
}

/**
 * Generic, reusable email masking helper.
 *
 * Examples with default options:
 * - "john.doe@example.com" → "jo*****@e***********"
 */
export function maskEmail(email: string, options: MaskEmailOptions = {}): string {
  const {
    localVisiblePrefix = 2,
    localVisibleSuffix = 0,
    domainVisiblePrefix = 1,
    domainVisibleSuffix = 0,
    maskChar = "*",
  } = options;

  const safeEmail = email.trim();
  const atIndex = safeEmail.indexOf("@");
  if (atIndex === -1) {
    // Not a valid email, just mask the whole string in a generic way
    return maskString(safeEmail, { visiblePrefix: 1, visibleSuffix: 0, maskChar });
  }

  const localPart = safeEmail.slice(0, atIndex);
  const domainPart = safeEmail.slice(atIndex + 1);

  const maskedLocal = maskString(localPart, {
    visiblePrefix: localVisiblePrefix,
    visibleSuffix: localVisibleSuffix,
    maskChar,
  });

  const maskedDomain = maskString(domainPart, {
    visiblePrefix: domainVisiblePrefix,
    visibleSuffix: domainVisibleSuffix,
    maskChar,
  });

  return `${maskedLocal}@${maskedDomain}`;
}

interface MaskStringOptions {
  visiblePrefix: number;
  visibleSuffix: number;
  maskChar: string;
}

function maskString(value: string, opts: MaskStringOptions): string {
  const { visiblePrefix, visibleSuffix, maskChar } = opts;
  if (!value) return value;

  const len = value.length;
  const start = Math.min(visiblePrefix, len);
  const end = Math.min(visibleSuffix, Math.max(0, len - start));

  const prefix = value.slice(0, start);
  const suffix = end > 0 ? value.slice(len - end) : "";
  const maskedLen = Math.max(0, len - start - end);

  if (maskedLen === 0) {
    // Nothing to mask
    return prefix + suffix;
  }

  return prefix + maskChar.repeat(maskedLen) + suffix;
}

export const addressSchema = z.object({
  fullName: z.string().trim().min(1, "Full Name is required"),
  contactNumber: z
    .string()
    .trim()
    .transform((val) => val.replace(/\D/g, ""))
    .refine((val) => /^[6-9][0-9]{9}$/.test(val), "Please enter a valid 10-digit mobile number"),
  address: z.string().trim().min(1, "Address is required"),
  street: z.string().trim().min(1, "Street is required"),
  locality: z.string().trim().min(1, "Locality is required"),
  landmark: z.string().optional(),
  postCode: z
    .string()
    .trim()
    .transform((val) => val.replace(/\D/g, ""))
    .refine((val) => /^[1-9][0-9]{5}$/.test(val), "Please enter a valid 6-digit pincode"),
  cityName: z.string().trim().min(1, "City is required"),
  stateId: z.string().trim().min(1, "State is required"),
});

type AddressSchemaType = z.infer<typeof addressSchema>;

export interface AddressFormValidationResult {
  success: boolean;
  errors: Partial<Record<keyof AddressSchemaType, string>>;
}

export function validateAddressForm(data: unknown): AddressFormValidationResult {
  const result = addressSchema.safeParse(data);
  
  if (result.success) {
    return { success: true, errors: {} };
  }

  // zods internal string arrays
  const fieldErrors = result.error.flatten().fieldErrors;
  const formattedErrors: Partial<Record<keyof AddressSchemaType, string>> = {};

  // convert array lookups into single clean string primitives
  const keys = Object.keys(fieldErrors) as (keyof AddressSchemaType)[];
  for (const key of keys) {
    const messages = fieldErrors[key];
    if (messages && messages.length > 0) {
      formattedErrors[key] = messages[0];
    }
  }

  return {
    success: false,
    errors: formattedErrors,
  };
}